import {
    type ProductGroupItem,
    renderProductCard
} from "@/app/(site)/_components/Organisms/ProductGrids/ProductGridCard"
import {
    getDesktopLoadingProps,
    getModuleStartColumn,
    type GridContentBlock,
    type RowGroup
} from "@/app/(site)/_components/Organisms/ProductGrids/types/grid-content"
import { clsx } from "@/src/utils/clsx"
import { Fragment, type ReactNode } from "react"

type ProductGridModuleContentProps = {
    desktopRowGroups: RowGroup<ProductGroupItem>[]
    mobileRowGroups: RowGroup<ProductGroupItem>[]
    renderedGridContent: Record<string, ReactNode>
    params: { locale: string; categories: string[] }
    listHierarchy: string
    desktopEagerCount: number
    desktopColumns: number
}

const MODULE_BASE = "col-span-full aspect-[2/3]"

const MODULE_START_CLASS: Record<number, string> = {
    1: "lg:col-start-1",
    2: "lg:col-start-2",
    3: "lg:col-start-3",
    4: "lg:col-start-4",
    5: "lg:col-start-5"
}

const hasValidPosition = (value: number) => Number.isFinite(value) && value > 0

const getModuleVisibilityClass = (module: GridContentBlock) => {
    const hasDesktop = hasValidPosition(module.positionRowDesktop)
    const hasMobile = hasValidPosition(module.positionRowMobile)

    if (hasDesktop && hasMobile) return ""
    if (hasDesktop && !hasMobile) return "hidden lg:block"
    if (!hasDesktop && hasMobile) return "lg:hidden"
    return "hidden"
}

const renderGridContent = ({
    gridContent,
    className,
    _key
}: {
    gridContent: ReactNode
    className?: string
    _key: string
}) => (
    <div
        className={clsx("grid-content-block", className, "lg:h-full")}
        key={_key}
        role="listitem"
    >
        {gridContent}
    </div>
)

const collectModules = (groups: RowGroup<ProductGroupItem>[]): GridContentBlock[] =>
    groups.flatMap(group => {
        if (group.type === "module") return [group.module]
        if (group.type === "fullWidth") return group.modules
        return []
    })

export default function ProductGridModuleContent({
    desktopRowGroups,
    mobileRowGroups,
    renderedGridContent,
    params,
    listHierarchy,
    desktopEagerCount,
    desktopColumns
}: ProductGridModuleContentProps) {
    const renderProducts = (group: {
        products: ProductGroupItem[]
        startIndex: number
    }) =>
        group.products.map((productGroup, i) =>
            renderProductCard({
                productGroup,
                index: group.startIndex + i,
                isAboveTheFold: getDesktopLoadingProps(
                    group.startIndex + i,
                    desktopEagerCount
                ).isAboveTheFold,
                params,
                listHierarchy,
                _key: productGroup.key
            })
        )

    const desktopElements = desktopRowGroups.map((group, groupIdx) => {
        const groupKey = `row-${groupIdx}`

        if (group.type === "fullWidth") {
            return (
                <Fragment key={groupKey}>
                    {group.modules.map(mod =>
                        renderGridContent({
                            gridContent: renderedGridContent[mod._key],
                            className: clsx(
                                `${MODULE_BASE} lg:aspect-[16/9]`,
                                getModuleVisibilityClass(mod)
                            ),
                            _key: mod._key
                        })
                    )}
                </Fragment>
            )
        }

        if (group.type === "module") {
            const moduleStart = getModuleStartColumn(
                group.module.horizontalAlignmentDesktop,
                desktopColumns
            )

            return (
                <Fragment key={groupKey}>
                    {group.module._key &&
                        renderedGridContent[group.module._key] &&
                        renderGridContent({
                            gridContent: renderedGridContent[group.module._key],
                            className: clsx(
                                `${MODULE_BASE} lg:aspect-auto lg:col-span-2`,
                                MODULE_START_CLASS[moduleStart] ?? "",
                                getModuleVisibilityClass(group.module)
                            ),
                            _key: group.module._key
                        })}
                    {renderProducts(group)}
                </Fragment>
            )
        }

        return <Fragment key={groupKey}>{renderProducts(group)}</Fragment>
    })

    const mobileOnlyElements = collectModules(mobileRowGroups)
        .filter(
            mod =>
                hasValidPosition(mod.positionRowMobile) &&
                !hasValidPosition(mod.positionRowDesktop) &&
                renderedGridContent[mod._key]
        )
        .map(mod =>
            renderGridContent({
                gridContent: renderedGridContent[mod._key],
                className: `${MODULE_BASE} lg:hidden`,
                _key: `${mod._key}-mobile`
            })
        )

    return (
        <>
            {desktopElements}
            {mobileOnlyElements}
        </>
    )
}
